import React, { useState, useEffect } from 'react';
import { Modal, Box, Checkbox, Button, Typography, List, ListItem, ListItemButton, ListItemText, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { get } from 'lodash';
import styled from 'styled-components';
import Loading from 'components/Loading';
import COLOR from 'utils/constants/color';

const ModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const HeaderSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLOR.gray100};
`;

const ActionButtons = styled(Box)`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const StudentList = styled(List)`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${COLOR.gray100};
  border-radius: 4px;
`;

const FooterSection = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${COLOR.gray100};
`;

const getUserName = (usrAssignment) => {
  const firstName = get(usrAssignment, "user.firstName", "");
  const lastName = get(usrAssignment, "user.lastName", "");
  return `${firstName} ${lastName}`.trim() || "Unknown Student";
};

const AiGradeAllModal = ({
  open,
  onClose,
  eligibleSubmissions,
  selectedSubmissions,
  onToggleSubmission,
  onSelectAll,
  onDeselectAll,
  onSubmit,
  loading
}) => {
  const [selectedGradeLevel, setSelectedGradeLevel] = useState('default');

  useEffect(() => {
    if (open) {
      setSelectedGradeLevel('default');
    }
  }, [open]);

  const handleToggle = (submissionId) => {
    onToggleSubmission(submissionId);
  };

  const handleSelectAll = () => {
    onSelectAll();
  };

  const handleDeselectAll = () => {
    onDeselectAll();
  };

  const handleSubmit = () => {
    const gradeLevelToSend = selectedGradeLevel === 'default' ? null : selectedGradeLevel;
    onSubmit(selectedSubmissions, gradeLevelToSend);
  };

  const allSelected = eligibleSubmissions.length > 0 && 
    selectedSubmissions.length === eligibleSubmissions.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="ai-grade-all-modal"
    >
      <ModalContent>
        <HeaderSection>
          <Typography variant="h6" component="h2">
            AI Grade to All
          </Typography>
          <Button onClick={onClose} size="small">
            Close
          </Button>
        </HeaderSection>

        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
          Select students to schedule AI grading. Selected: {selectedSubmissions.length} of {eligibleSubmissions.length}
        </Typography>

        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
          <InputLabel id="grade_level_select" style={{ background: 'white' }}>
            Level for AI grading
          </InputLabel>
          <Select
            labelId="grade_level_select"
            id="grade_level_select"
            value={selectedGradeLevel || 'default'}
            onChange={(e) => setSelectedGradeLevel(e.target.value)}
            label="Level for AI grading"
            disabled={loading}
          >
            <MenuItem value={'default'}>Default</MenuItem>
            <MenuItem value={'NoviceLow'}>Novice low</MenuItem>
            <MenuItem value={'NoviceMid'}>Novice mid</MenuItem>
            <MenuItem value={'NoviceHigh'}>Novice high</MenuItem>

            <MenuItem value={'IntermediateLow'}>Intermediate low</MenuItem>
            <MenuItem value={'IntermediateMid'}>Intermediate mid</MenuItem>
            <MenuItem value={'IntermediateHigh'}>Intermediate high</MenuItem>

            <MenuItem value={'AdvanceLow'}>Advance low</MenuItem>
            <MenuItem value={'AdvanceMid'}>Advance mid</MenuItem>
            <MenuItem value={'AdvanceHigh'}>Advance high</MenuItem>
          </Select>
        </FormControl>

        <ActionButtons>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={allSelected || loading}
          >
            Select All
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeselectAll}
            disabled={selectedSubmissions.length === 0 || loading}
          >
            Deselect All
          </Button>
        </ActionButtons>

        {eligibleSubmissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" style={{ textAlign: 'center', padding: '20px' }}>
            No eligible students found. Students must have completed assignments with answers.
          </Typography>
        ) : (
          <StudentList>
            {eligibleSubmissions.map((submission) => {
              const submissionId = submission.id;
              const isSelected = selectedSubmissions.includes(submissionId);
              const studentName = getUserName(submission);

              return (
                <ListItem
                  key={submissionId}
                  disablePadding
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={isSelected}
                      onChange={() => handleToggle(submissionId)}
                      disabled={loading}
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => handleToggle(submissionId)}
                    disabled={loading}
                  >
                    <ListItemText primary={studentName} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </StudentList>
        )}

        <FooterSection>
          <Button
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={selectedSubmissions.length === 0 || loading}
            startIcon={loading ? <Loading white size={12} /> : null}
          >
            {loading ? 'Scheduling...' : 'Schedule AI Grading'}
          </Button>
        </FooterSection>
      </ModalContent>
    </Modal>
  );
};

export default AiGradeAllModal;

